import React, { useEffect, useState } from 'react';
import { RefreshCw, Edit2, Trash2, ArrowRightLeft } from 'lucide-react';
import { matchIngredient } from '../../utils/foodStickers';

// Compact ingredient table for the meal builder (main dish, alternatives, AI suggestion).
// DESIGN.md: hairline rows, tabular numbers. Macros are ink like kcal; the column header names them.
// Actions are always visible (never hover-only) and every icon button is labelled.

const COPY = {
  english: { move: 'Move to another meal', moveTo: 'Move to', cancel: 'Cancel', ingredient: 'Ingredient', portion: 'Grams', replace: 'Replace', edit: 'More portion options', remove: 'Remove', empty: 'No ingredients yet. Draft the day, or add an ingredient.', unknown: 'Unnamed item' },
  hebrew: { move: 'העברה לארוחה אחרת', moveTo: 'העברה אל', cancel: 'ביטול', ingredient: 'מרכיב', portion: 'גרם', replace: 'החלף', edit: 'אפשרויות מנה', remove: 'הסר', empty: 'אין מרכיבים עדיין. נסחו את היום, או הוסיפו מרכיב.', unknown: 'פריט ללא שם' },
};

// The ingredient's own food sticker (or its food group's). Seasonings and oil get an empty slot so names line up.
function IngredientSticker({ name }) {
  const [broken, setBroken] = useState(false);
  const m = matchIngredient(name);
  const url = m && !m.skip ? m.sticker?.url : null;
  const badge = url ? m.sticker?.badge?.url : null;
  return (
    <span aria-hidden="true" className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center">
      {url && !broken ? (
        <>
          <img src={url} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setBroken(true)} className={`h-7 w-7 object-contain ${m.exact ? '' : 'opacity-60'}`} />
          {badge && (
            <span className="absolute -bottom-1 -end-1 flex h-4 w-4 items-center justify-center rounded-full bg-surface ring-1 ring-line dark:bg-surface-dark dark:ring-line-dark">
              <img src={badge} alt="" loading="lazy" decoding="async" draggable={false} className="h-3 w-3 object-contain" />
            </span>
          )}
        </>
      ) : (
        <span className="h-1.5 w-1.5 rounded-full bg-line dark:bg-line-dark" />
      )}
    </span>
  );
}

const fmt = (n, digits = 0) => {
  const v = Number(n) || 0;
  return digits ? parseFloat(v.toFixed(digits)) : Math.round(v);
};

const portionLabel = (ing) => {
  const grams = ing['portionSI(gram)'];
  const g = grams ? `${fmt(grams, 1)}g` : '';
  if (ing.household_measure) return g ? `${ing.household_measure} (${g})` : ing.household_measure;
  return g || '—';
};

function GramsField({ grams, name, label, disabled, onCommit }) {
  const [text, setText] = useState(grams == null || grams === '' ? '' : String(grams));
  useEffect(() => {
    setText(grams == null || grams === '' ? '' : String(grams));
  }, [grams]);

  const commit = () => {
    const n = Number(text);
    if (!String(text).trim() || !(n >= 1)) {
      setText(grams == null || grams === '' ? '' : String(grams));
      return;
    }
    if (n !== Number(grams)) onCommit(n);
  };

  return (
    <input
      type="number"
      inputMode="numeric"
      min="1"
      disabled={disabled}
      aria-label={`${label}: ${name}`}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className="min-h-11 w-20 rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-2 py-1 text-end text-[13px] tabular-nums text-ink dark:text-ink-dark focus:outline-none focus:ring-2 focus:ring-forest disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
}

export default function IngredientTable({ ingredients, language = 'english', onReplace, onEdit, onDelete, onGramsChange, gramsDisabled = false, moveTargets = [], onMove }) {
  const [movingRow, setMovingRow] = useState(null);
  const canMove = !!onMove && moveTargets.length > 0;
  const c = COPY[language] || COPY.english;
  const list = Array.isArray(ingredients) ? ingredients : [];

  if (!list.length) {
    return (
      <p className="rounded-lg border border-dashed border-line dark:border-line-dark px-4 py-5 text-center text-[13px] text-ink-muted dark:text-ink-muted-dark">
        {c.empty}
      </p>
    );
  }

  const btn = 'inline-flex h-11 w-11 items-center justify-center rounded-md text-ink-muted dark:text-ink-muted-dark transition-colors duration-120 hover:bg-moss hover:text-ink dark:hover:bg-moss-dark dark:hover:text-ink-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest';
  const hasActions = !!(onReplace || onEdit || onDelete || canMove);

  const renderActions = (idx, ing, name, cls = btn) => (
    <>
      {canMove && (
        <button type="button" className={cls} onClick={() => setMovingRow(movingRow === idx ? null : idx)} aria-expanded={movingRow === idx} aria-label={`${c.move}: ${name}`} title={c.move}>
          <ArrowRightLeft size={14} aria-hidden="true" />
        </button>
      )}
      {onReplace && (
        <button type="button" className={cls} onClick={() => onReplace(idx)} aria-label={`${c.replace}: ${name}`} title={c.replace}>
          <RefreshCw size={14} aria-hidden="true" />
        </button>
      )}
      {onEdit && (
        <button type="button" className={cls} onClick={() => onEdit(idx, ing)} aria-label={`${c.edit}: ${name}`} title={c.edit}>
          <Edit2 size={14} aria-hidden="true" />
        </button>
      )}
      {onDelete && (
        <button type="button" className={`${cls} hover:!text-red-700 dark:hover:!text-red-400`} onClick={() => onDelete(idx)} aria-label={`${c.remove}: ${name}`} title={c.remove}>
          <Trash2 size={14} aria-hidden="true" />
        </button>
      )}
    </>
  );

  const renderMoveRow = (idx) => (
    <span className="flex flex-wrap items-center gap-2" onKeyDown={(e) => { if (e.key === 'Escape') setMovingRow(null); }}>
      <span className="text-[12px] text-ink-muted dark:text-ink-muted-dark">{c.moveTo}</span>
      {moveTargets.map((target) => (
        <button
          key={target.index}
          type="button"
          onClick={() => { setMovingRow(null); onMove(idx, target.index); }}
          className="min-h-11 rounded-md border border-line bg-surface px-3 text-[13px] font-medium text-ink hover:bg-moss focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest dark:border-line-dark dark:bg-surface-dark dark:text-ink-dark dark:hover:bg-moss-dark"
        >
          {target.label}
        </button>
      ))}
      <button type="button" onClick={() => setMovingRow(null)} className="min-h-11 px-2 text-[13px] font-medium text-ink-muted underline underline-offset-4 hover:text-ink dark:text-ink-muted-dark dark:hover:text-ink-dark">
        {c.cancel}
      </button>
    </span>
  );

  return (
    <>
    {/* Phones: one stacked row per food, nothing scrolls sideways. */}
    <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line text-[13px] dark:divide-line-dark dark:border-line-dark sm:hidden">
      {list.map((ing, idx) => {
        const name = ing.item || ing.name || c.unknown;
        return (
          <li key={idx} className="px-3 py-2.5">
            <div className="flex items-start gap-2.5">
              <IngredientSticker name={ing.item || ing.name} />
              <div className="min-w-0 flex-1">
                <p className="font-medium leading-snug text-ink dark:text-ink-dark">{name}</p>
                <bdi dir="ltr" className="block text-[12px] tabular-nums text-ink-muted dark:text-ink-muted-dark">P {fmt(ing.protein, 1)} · C {fmt(ing.carbs, 1)} · F {fmt(ing.fat, 1)}</bdi>
              </div>
              <span className="shrink-0 pt-0.5 font-semibold tabular-nums text-ink dark:text-ink-dark">{fmt(ing.calories)} <span className="text-[11px] font-normal text-ink-muted dark:text-ink-muted-dark">kcal</span></span>
            </div>
            <div className="mt-1.5 flex items-center gap-1 ps-[2.375rem]">
              {onGramsChange ? (
                <span className="inline-flex items-center gap-1.5">
                  <GramsField grams={ing['portionSI(gram)']} name={name} label={c.portion} disabled={gramsDisabled} onCommit={(grams) => onGramsChange(idx, grams)} />
                  <span className="text-[12px] text-ink-muted dark:text-ink-muted-dark">g</span>
                </span>
              ) : (
                <span className="text-ink-muted dark:text-ink-muted-dark">{portionLabel(ing)}</span>
              )}
              {hasActions && <span className="ms-auto inline-flex">{renderActions(idx, ing, name, btn.replace('h-11 w-11', 'h-10 w-10'))}</span>}
            </div>
            {movingRow === idx && <div className="mt-2 ps-[2.375rem]">{renderMoveRow(idx)}</div>}
          </li>
        );
      })}
    </ul>

    <div className="hidden overflow-x-auto rounded-lg border border-line dark:border-line-dark sm:block">
      <table className="w-full min-w-[560px] text-[13px]">
        <thead className="bg-canvas dark:bg-canvas-dark text-[11px] text-ink-muted dark:text-ink-muted-dark">
          <tr>
            <th scope="col" className="px-3 py-2 text-start font-medium">{c.ingredient}</th>
            <th scope="col" className="px-3 py-2 text-start font-medium">{c.portion}</th>
            <th scope="col" className="px-2 py-2 text-end font-medium">kcal</th>
            <th scope="col" className="px-2 py-2 text-end font-medium">P</th>
            <th scope="col" className="px-2 py-2 text-end font-medium">C</th>
            <th scope="col" className="px-2 py-2 text-end font-medium">F</th>
            {hasActions && <th scope="col" className="px-2 py-2"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {list.map((ing, idx) => {
            const name = ing.item || ing.name || c.unknown;
            return (
              <React.Fragment key={idx}>
              <tr className="border-t border-line dark:border-line-dark hover:bg-canvas/60 dark:hover:bg-canvas-dark/60">
                <td className="px-3 py-2 font-medium text-ink dark:text-ink-dark">
                  <span className="flex items-center gap-2.5"><IngredientSticker name={ing.item || ing.name} />{name}</span>
                </td>
                <td className="px-3 py-2 text-ink-muted dark:text-ink-muted-dark whitespace-nowrap">
                  {onGramsChange ? (
                    <GramsField
                      grams={ing['portionSI(gram)']}
                      name={name}
                      label={c.portion}
                      disabled={gramsDisabled}
                      onCommit={(grams) => onGramsChange(idx, grams)}
                    />
                  ) : portionLabel(ing)}
                </td>
                <td className="px-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{fmt(ing.calories)}</td>
                <td className="px-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{fmt(ing.protein, 1)}</td>
                <td className="px-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{fmt(ing.carbs, 1)}</td>
                <td className="px-2 py-2 text-end tabular-nums text-ink dark:text-ink-dark">{fmt(ing.fat, 1)}</td>
                {hasActions && (
                  <td className="px-1 py-1 text-end whitespace-nowrap">{renderActions(idx, ing, name)}</td>
                )}
              </tr>
              {movingRow === idx && (
                <tr className="bg-canvas/60 dark:bg-canvas-dark/60">
                  <td colSpan={7} className="px-3 py-2">{renderMoveRow(idx)}</td>
                </tr>
              )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}
